import { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, liveMatches, newsArticles } from '@/mocks/sports';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeTab, setActiveTab] = useState<'matches' | 'news'>('matches');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');

  useEffect(() => {
    setActiveTab('matches');
    setSelectedStatus('all');
  }, [slug]);

  const category = categories.find((c) => c.slug === slug) || categories[0];

  const filteredMatches = useMemo(() => {
    return liveMatches.filter((m) => {
      if (m.category !== slug) return false;
      if (selectedStatus === 'all') return true;
      return m.status === selectedStatus;
    });
  }, [slug, selectedStatus]);

  const filteredNews = useMemo(() => {
    return newsArticles.filter((a) => a.category === slug);
  }, [slug]);

  const statusTabs = [
    { key: 'all', label: '全部' },
    { key: 'live', label: '直播中' },
    { key: 'upcoming', label: '即将开始' },
    { key: 'finished', label: '已结束' },
  ];

  const seoTitle = `${category.name}赛事直播 - 米乐体育官方平台`;
  const seoDesc = `米乐体育为您提供${category.name}赛事直播、实时比分、赛程安排和专业数据分析。${category.description}`;
  const seoKeywords = `米乐体育,${category.name},${category.name}直播,${category.name}赛事,${category.nameEn}`;

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        keywords={seoKeywords}
        canonicalUrl={`/category/${slug}`}
        ogType="website"
      />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />
            <div className="pb-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center text-primary-500">
                  <i className={`${category.icon} text-lg`}></i>
                </div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">{category.name}</h1>
              </div>
              <p className="text-sm text-foreground-500">{category.description}</p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <div className="flex items-center gap-3 mb-6">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => setActiveTab('matches')}
              className={`text-sm font-semibold pb-2 border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'matches'
                  ? 'border-primary-500 text-primary-500'
                  : 'border-transparent text-foreground-500 hover:text-foreground-700'
              }`}
            >
              赛事直播
            </button>
            <button
              onClick={() => setActiveTab('news')}
              className={`text-sm font-semibold pb-2 border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'news'
                  ? 'border-primary-500 text-primary-500'
                  : 'border-transparent text-foreground-500 hover:text-foreground-700'
              }`}
            >
              相关资讯
            </button>
          </div>

          {activeTab === 'matches' && (
            <>
              <div className="flex items-center gap-2 mb-5 flex-wrap">
                {statusTabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setSelectedStatus(tab.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      selectedStatus === tab.key
                        ? 'bg-accent-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {filteredMatches.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-live-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">暂无相关赛事</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                  {filteredMatches.map((match) => (
                    <div
                      key={match.id}
                      className="bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:shadow-md transition-all duration-200"
                    >
                      <div className="relative h-44 overflow-hidden">
                        <img
                          src={match.thumbnail}
                          alt={`${match.homeTeam} vs ${match.awayTeam}`}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        {match.status === 'live' && (
                          <div className="absolute top-3 left-3">
                            <span className="bg-primary-500 text-background-50 text-xs font-semibold px-2 py-0.5 rounded flex items-center gap-1">
                              <span className="w-1.5 h-1.5 bg-background-50 rounded-full animate-pulse"></span>
                              直播中
                            </span>
                          </div>
                        )}
                        {match.status === 'upcoming' && (
                          <div className="absolute top-3 left-3">
                            <span className="bg-accent-500 text-background-50 text-xs font-semibold px-2 py-0.5 rounded">
                              即将开始
                            </span>
                          </div>
                        )}
                        {match.status === 'finished' && (
                          <div className="absolute top-3 left-3">
                            <span className="bg-secondary-500 text-background-50 text-xs font-semibold px-2 py-0.5 rounded">
                              已结束
                            </span>
                          </div>
                        )}
                        {match.viewers > 0 && (
                          <div className="absolute top-3 right-3">
                            <span className="bg-black/60 text-white text-xs px-2 py-0.5 rounded">
                              {match.viewers.toLocaleString()} 人观看
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <p className="text-xs text-foreground-500 mb-2">{match.league}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex-1 text-center">
                            <p className="font-semibold text-foreground-900 text-sm">{match.homeTeam}</p>
                          </div>
                          <div className="px-3">
                            <span className="font-heading text-xl font-bold text-foreground-900">
                              {match.status === 'upcoming' ? 'VS' : `${match.homeScore} : ${match.awayScore}`}
                            </span>
                          </div>
                          <div className="flex-1 text-center">
                            <p className="font-semibold text-foreground-900 text-sm">{match.awayTeam}</p>
                          </div>
                        </div>
                        <p className={`text-xs font-medium mt-2 text-center ${match.status === 'live' ? 'text-primary-500' : 'text-foreground-500'}`}>
                          {match.minute}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {activeTab === 'news' && (
            <>
              {filteredNews.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                    <i className="ri-newspaper-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500 text-sm">暂无相关资讯</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                  {filteredNews.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:shadow-md transition-all duration-200 cursor-pointer flex flex-col sm:flex-row"
                    >
                      <div className="w-full sm:w-48 h-36 sm:h-auto flex-shrink-0 overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4 flex-1 flex flex-col justify-center">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs text-foreground-400">{article.date}</span>
                          <span className="text-xs text-foreground-400">{article.readTime}阅读</span>
                        </div>
                        <h3 className="font-semibold text-sm md:text-base text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors leading-snug">
                          {article.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-2 line-clamp-2">{article.excerpt}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </>
  );
}